import * as React from 'react';

import gql from 'graphql-tag';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import { LONG_DATETIME_DISPLAY, formatDateTimeDisplay, humanizeLearningTime } from 'js/utils/DateTimeUtils';

import ProgressIndicator from 'bundles/degree-home/components/course-section/ProgressIndicator';
import TimeCommitment from 'bundles/degree-home/components/course-section/TimeCommitment';
import type { Item } from 'bundles/degree-home/types/Course';
import { isGradedItemOutcome } from 'bundles/degree-home/types/Course';
import {
  getFormattedShortDate,
  getItemLink,
  getProgressStateLabel,
  getTitlePrefix,
  isOverdue,
} from 'bundles/degree-home/utils/ItemState';
import ItemIcon from 'bundles/item/components/ItemIcon';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

import 'css!./__styl__/DueItem';

type Props = {
  item: Item;
  courseSlug: string;
};

class DueItem extends React.Component<Props> {
  static fragment = gql`
    fragment DueItemFragment on DegreeLearnerProgressesV1_org_coursera_degree_learner_ItemState {
      id
      name
      timeCommitment
      ...TitlePrefixFragment
      ...LinkFragment
      ...DueAtFragment
      ...OverdueFragment
      ...ProgressIndicatorFragment
    }
    ${getTitlePrefix.fragment}
    ${getItemLink.fragment}
    ${getFormattedShortDate.fragment}
    ${isOverdue.fragment}
    ${ProgressIndicator.fragment}
  `;

  generateLabelFromDueItem() {
    let dueItemAriaLabel = '';
    const { item } = this.props;
    const titlePrefix = getTitlePrefix(item);

    if (titlePrefix) {
      dueItemAriaLabel = `${titlePrefix} :`;
    }

    dueItemAriaLabel += `
      ${item.name},
      ${humanizeLearningTime(item.timeCommitment, true)},
      ${formatDateTimeDisplay(item.deadline.dueAt, LONG_DATETIME_DISPLAY)},
    `;

    const { computedOutcome, computedProgress } = item;

    if (computedOutcome) {
      const itemOutcome = computedOutcome.org_coursera_degree_learner_Outcome;

      if (itemOutcome && isGradedItemOutcome(itemOutcome)) {
        dueItemAriaLabel += ` ${Math.round(itemOutcome.grade * 100)}%`;
      } else if (computedProgress.progressState === 'InReview' || computedProgress.progressState === 'Optional') {
        dueItemAriaLabel += ` ${getProgressStateLabel(computedProgress.progressState)}`;
      } else if (isOverdue(item)) {
        dueItemAriaLabel += ` ${getProgressStateLabel('Overdue')}`;
      }
    }

    return dueItemAriaLabel;
  }

  render() {
    const { item, courseSlug } = this.props;
    const titlePrefix = getTitlePrefix(item);

    const dueItemAriaLabel = this.generateLabelFromDueItem();

    return (
      <TrackedA
        className="rc-DueItem nostyle horizontal-box align-items-spacebetween p-x-2"
        href={getItemLink(item, courseSlug)}
        trackingName="item_link"
        aria-label={dueItemAriaLabel}
      >
        <div className="horizontal-box align-items-vertical-center">
          <ItemIcon size={24} style={{ marginRight: 12 }} type={item.typeName} />
          <div className="item-text-container">
            <span className="inline-child">
              {titlePrefix ? (
                <FormattedHTMLMessage message={`<strong>${titlePrefix}: </strong> ${item.name}`} />
              ) : (
                <FormattedHTMLMessage message={`${item.name}`} />
              )}
            </span>

            <TimeCommitment className="inline-child" timeCommitment={item.timeCommitment} />
            <div className="due-date-pill inline-child">
              <div className="pill-light">{getFormattedShortDate(item)}</div>
            </div>
          </div>
        </div>

        <ProgressIndicator
          typeName={item.typeName}
          isOverdue={isOverdue(item)}
          computedProgress={item.computedProgress}
          computedOutcome={item.computedOutcome}
        />
      </TrackedA>
    );
  }
}

export default DueItem;
